import { useHotkey } from "@tanstack/react-hotkeys";
import { useCallback, useState } from "react";

import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { useHasUnactedOwnUnits } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

function EndTurnConfirmDialog({
  open,
  onDismiss,
  onConfirm,
  pending,
}: {
  open: boolean;
  onDismiss: () => void;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DialogContent showCloseButton={false} initialFocus={false}>
        <DialogHeader>
          <DialogTitle>End turn?</DialogTitle>
          <DialogDescription>You still have units that can move or attack.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={() => void onConfirm()}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useEndTurnConfirmation({
  onConfirm,
  pending,
  confirmBeforeEndTurn = true,
}: {
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
  confirmBeforeEndTurn?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasUnactedOwnUnits = useHasUnactedOwnUnits();
  const showUnmovedHighlight = useGameSelector((s) => s.showUnmovedHighlight);
  const startUnmovedHighlightFade = useGameSelector((s) => s.startUnmovedHighlightFade);
  const clearUnmovedHighlight = useGameSelector((s) => s.clearUnmovedHighlight);

  const requestEndTurn = useCallback(() => {
    if (confirmBeforeEndTurn && hasUnactedOwnUnits) {
      showUnmovedHighlight();
      setOpen(true);
      return;
    }
    void onConfirm();
  }, [confirmBeforeEndTurn, hasUnactedOwnUnits, onConfirm, showUnmovedHighlight]);

  const dismiss = useCallback(() => {
    startUnmovedHighlightFade();
    setOpen(false);
  }, [startUnmovedHighlightFade]);

  const confirmAndClose = useCallback(async () => {
    clearUnmovedHighlight();
    setOpen(false);
    await onConfirm();
  }, [clearUnmovedHighlight, onConfirm]);

  useHotkey(
    "Space",
    (event) => {
      if (!open || pending) return;
      event.preventDefault();
      void confirmAndClose();
    },
    { enabled: open, ignoreInputs: false, conflictBehavior: "allow" },
  );

  const confirmDialog = (
    <EndTurnConfirmDialog
      open={open}
      onDismiss={dismiss}
      onConfirm={confirmAndClose}
      pending={pending}
    />
  );

  return { requestEndTurn, confirmDialog, dialogOpen: open };
}
